import React, { useState } from 'react';
import {
  Drawer,
  ListItemButton,
  ListItemText,
  Box,
  Toolbar,
  Divider,
  Typography,
  Collapse,
  AppBar,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MoodBadIcon from '@mui/icons-material/MoodBad';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CalculateIcon from '@mui/icons-material/Calculate';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupsIcon from '@mui/icons-material/Groups';
import { useLocation, useNavigate } from 'react-router-dom';
import Emproutes from './Emproutes';
import EmployeeTopNav from './EmployeeTopNav';
import AskHRWidget from './AskHRWidget';
import AiHrAssistant from './AiHrAssistant';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import LogoutIcon from '@mui/icons-material/Logout';
import { getDirectTeamMembers } from '../../api';
import { AuthContext } from '../../context/logeduser';
import { getInitials } from '../../utils/initials';
import { useCompany } from '../../context/CompanyContext';

const drawerWidth = 240;
// Primary accent shared with /home, /attendance, /payslip, /profile, /leaves …
const ACCENT = '#2f6bff';

export default function ResponsiveDrawer() {
  const [isManager, setIsManager] = useState(false);
  const [form16Open, setForm16Open] = useState(false);
  const [, setFetchingManager] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = React.useContext(AuthContext);
  const theme = useTheme();
  const company = useCompany();
  // Below md the permanent 240px rail would eat the screen, so on phones/tablets
  // the rail becomes an off-canvas drawer toggled by a top app-bar hamburger.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Logout is reachable from EVERY page via the rail footer (no need to go Home
  // → click name → Log Out). Mirrors the profile-menu logout on /home.
  const handleLogout = () => {
    if (typeof logout === 'function') logout();
    navigate('/login');
  };
  const authDataString = localStorage.getItem('authData');
  const authData = authDataString ? JSON.parse(authDataString) : null;
  const id = authData?.employee_no;

  React.useEffect(() => {
    const checkManagerStatus = async () => {
      if (!id) return;

      // If the user has an admin token, they might be considered a manager or have higher privileges
      if (authData?.admin?.Token) {
        setIsManager(true);
        setFetchingManager(false);
        return;
      }

      // api/managers/ is Admin/HR-only, so an employee token gets a 403 there —
      // and inside Promise.all that rejection used to discard the team roster
      // too, hiding Team Leaves / Team Attendance from every manager and team
      // lead. The direct-team roster is resolved server-side from the verified
      // token (manager_id / reporting_manager / team_head — the same match the
      // team pages use), so it alone decides whether the team menu is shown.
      try {
        const directTeam = await getDirectTeamMembers();
        setIsManager(Array.isArray(directTeam) && directTeam.length > 0);
      } catch (err) {
        console.error('Failed to fetch direct team:', err);
      } finally {
        setFetchingManager(false);
      }
    };
    checkManagerStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authDataString]);

  // Form 16 children render as a collapsible group (nav items live in navGroups).
  const form16Children = ['My Declarations', 'My Form 16', 'Rectify Form 16'];

  const handleNavigation = (route) => {
    navigate(route);
    setMobileOpen(false); // close the off-canvas drawer after navigating on mobile
  };

  const icons = {
    Home: <HomeIcon />,
    Attendance: <EventAvailableIcon />,
    Payslip: <ReceiptIcon />,
    Profile: <AccountCircleIcon />,
    Leaves: <MoodBadIcon />,
    'Tax Calculator': <CalculateIcon />,
    'Team Leaves': <Diversity3Icon />,
    'Team Attendance': <GroupsIcon />,
    'My Declarations': <AssignmentIcon />,
    'My Form 16': <DescriptionIcon />,
    'Rectify Form 16': <ReportProblemIcon />,
  };

  const routes = {
    Home: '/home',
    Attendance: '/attendance',
    Payslip: '/payslip',
    Profile: '/profile',
    Leaves: '/leaves',
    'Tax Calculator': '/tax-calculator',
    'Team Leaves': '/team-leaves',
    'Team Attendance': '/team-attendance',
    'My Declarations': '/declarations',
    'My Form 16': '/form16',
    'Rectify Form 16': '/form16/rectify',
  };

  // Profile and Form 16 pages carry their own identity header, so the shared
  // top bar is hidden there to avoid a duplicate.
  const hideTopNav = ['/profile', '/form16', '/declarations'].some((p) => location.pathname.startsWith(p));

  // ---- Sidebar item styling (mockup: icon tile + accent-soft active pill) ----
  const isActive = (text) => location.pathname === routes[text];
  const groupLabelSx = {
    fontSize: '0.62rem', letterSpacing: '0.09em', textTransform: 'uppercase',
    color: '#94a0b0', fontWeight: 700, px: 1.5, pt: 1.25, pb: 0.5, whiteSpace: 'nowrap',
  };
  // One item's sx — `child` shrinks the Form 16 sub-items.
  const itemSx = (active, child = false) => {
    const tile = child ? 26 : 32;
    return {
      gap: child ? 1 : 1.25,
      pl: child ? 2.25 : 1.25, pr: 1.25, py: child ? 0.6 : 0.85, mb: 0.35,
      borderRadius: '12px', position: 'relative',
      color: active ? '#0e1420' : '#5a6473',
      bgcolor: active ? '#eef4ff' : 'transparent',
      boxShadow: active ? '0 1px 2px rgba(16,24,40,0.05)' : 'none',
      transition: 'background .16s, color .16s',
      '& .nav-ic': {
        width: tile, height: tile, borderRadius: child ? '8px' : '9px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: active ? '#fff' : 'transparent',
        color: active ? ACCENT : (child ? '#94a3b8' : '#64748b'),
        boxShadow: active ? '0 1px 3px rgba(16,24,40,0.12)' : 'none',
        transition: '.16s',
        '& .MuiSvgIcon-root': { fontSize: child ? 17 : 20 },
      },
      '&:hover': {
        bgcolor: active ? '#eef4ff' : 'rgba(17,24,39,0.045)',
        color: '#0e1420',
        '& .nav-ic': active ? {} : { bgcolor: 'rgba(17,24,39,0.05)' },
      },
      // Left accent bar on the active top-level item.
      ...(active && !child && {
        '&::before': {
          content: '""', position: 'absolute', left: 3, top: '50%', transform: 'translateY(-50%)',
          width: 3, height: 18, borderRadius: 3, bgcolor: ACCENT,
        },
      }),
    };
  };
  const renderNavItem = (text) => {
    const active = isActive(text);
    return (
      <ListItemButton key={text} onClick={() => handleNavigation(routes[text])} sx={itemSx(active)}>
        <Box className="nav-ic">{icons[text]}</Box>
        <ListItemText
          primary={text}
          primaryTypographyProps={{ sx: { fontWeight: active ? 600 : 500, fontSize: '0.9rem', color: 'inherit' } }}
          sx={{ my: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        />
      </ListItemButton>
    );
  };
  // Presentational grouping (routes unchanged); Form 16 stays a collapsible group.
  const navGroups = [
    { label: 'Main', items: ['Home', 'Attendance', 'Leaves', ...(isManager ? ['Team Leaves', 'Team Attendance'] : [])] },
    { label: 'Payroll', items: ['Payslip', 'Tax Calculator'], form16: true },
    { label: 'Account', items: ['Profile'] },
  ];
  const form16Active = location.pathname.startsWith('/form16') || location.pathname === '/declarations';
  const userName = authData?.Employee?.name || authData?.name || authData?.employee?.name || 'Employee';
  const initials = getInitials(userName);

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      {/* ATS brand */}
      <Box
        onClick={() => handleNavigation('/home')}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', px: 1.25, py: 1.5, mb: 0.5 }}
      >
        {!company.loading && company.logo && <Box component="img" src={company.logo} alt={company.shortName || company.name || 'Company logo'} onError={(event) => { event.currentTarget.style.display = 'none'; }} sx={{ height: 34, width: 'auto', flexShrink: 0 }} />}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', letterSpacing: 0.3, whiteSpace: 'nowrap', lineHeight: 1.15 }}>
            <Box component="span" sx={{ color: '#1f2937' }}>ATS</Box>
            <Box component="span" sx={{ color: '#3DAC5A', ml: 0.5 }}>HR</Box>
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
            Aditya Trading
          </Typography>
        </Box>
      </Box>

      {/* Grouped nav */}
      <Box sx={{ px: 1 }}>
        {navGroups.map((g) => (
          <Box key={g.label} sx={{ mb: 0.5 }}>
            <Typography sx={groupLabelSx}>{g.label}</Typography>
            {g.items.map(renderNavItem)}
            {g.form16 && (
              <>
                <ListItemButton onClick={() => setForm16Open((v) => !v)} sx={itemSx(form16Active)}>
                  <Box className="nav-ic"><DescriptionIcon /></Box>
                  <ListItemText
                    primary="Form 16"
                    primaryTypographyProps={{ sx: { fontWeight: form16Active ? 600 : 500, fontSize: '0.9rem', color: 'inherit' } }}
                    sx={{ my: 0, whiteSpace: 'nowrap' }}
                  />
                  {form16Open ? <ExpandLess sx={{ color: '#94a3b8', fontSize: 20 }} /> : <ExpandMore sx={{ color: '#94a3b8', fontSize: 20 }} />}
                </ListItemButton>
                <Collapse in={form16Open} timeout="auto" unmountOnExit>
                  {form16Children.map((text) => {
                    const active = isActive(text);
                    return (
                      <ListItemButton key={text} onClick={() => handleNavigation(routes[text])} sx={itemSx(active, true)}>
                        <Box className="nav-ic">{icons[text]}</Box>
                        <ListItemText
                          primary={text}
                          primaryTypographyProps={{ sx: { fontWeight: active ? 600 : 500, fontSize: '0.82rem', color: 'inherit' } }}
                          sx={{ my: 0, whiteSpace: 'nowrap' }}
                        />
                      </ListItemButton>
                    );
                  })}
                </Collapse>
              </>
            )}
          </Box>
        ))}
      </Box>

      {/* Footer: user card + Log Out — pinned to the bottom, available on every page. */}
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ px: 1, pb: 1.5 }}>
        <Divider sx={{ mb: 1, borderColor: '#eef0f4' }} />
        <Box
          onClick={() => handleNavigation('/profile')}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1, mb: 0.5, borderRadius: '12px', cursor: 'pointer', transition: 'background .15s', '&:hover': { bgcolor: 'rgba(17,24,39,0.045)' } }}
        >
          <Box sx={{ width: 36, height: 36, borderRadius: '11px', flexShrink: 0, position: 'relative', background: 'linear-gradient(135deg,#1E293B,#0F172A)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.78rem' }}>
            {initials}
            <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 11, height: 11, borderRadius: '50%', bgcolor: '#22c55e', border: '2px solid #fff' }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0e1420', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#94a0b0', fontWeight: 500, whiteSpace: 'nowrap' }}>{id ? `#${id}` : 'View profile'}</Typography>
          </Box>
        </Box>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            gap: 1.25, px: 1.25, py: 0.85, borderRadius: '12px', color: '#dc2626',
            '& .nav-ic': { width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', '& .MuiSvgIcon-root': { fontSize: 19 } },
            '&:hover': { bgcolor: '#fef2f2' },
          }}
        >
          <Box className="nav-ic"><LogoutIcon /></Box>
          <ListItemText primary="Log Out" primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '0.9rem', color: 'inherit' } }} sx={{ my: 0 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  // Shared paper styling for both the permanent (desktop) and temporary (mobile) rail.
  const drawerPaperSx = {
    width: drawerWidth,
    boxSizing: 'border-box',
    overflowX: 'hidden',
    marginTop: 0,
    background: 'linear-gradient(180deg,#FFFFFF 0%,#FAFBFD 55%,#F6F8FB 100%)',
    borderRight: '1px solid #eef0f4',
    // Use the dynamic viewport height where supported so the bottom-pinned
    // Log Out isn't hidden behind the mobile browser's address/bottom bars
    // (plain 100vh spans behind them). Falls back to 100vh on old browsers.
    height: '100vh',
    '@supports (height: 100dvh)': { height: '100dvh' },
    // Square edges — the theme's default MuiPaper radius (20px) would otherwise
    // round the rail/header corners and make them look like a floating card.
    borderRadius: 0,
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile-only top app bar with a hamburger that opens the off-canvas rail. */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{ bgcolor: '#fff', color: '#111827', borderBottom: '1px solid #eef0f4', borderRadius: 0, zIndex: (t) => t.zIndex.drawer + 1 }}
        >
          <Toolbar sx={{ gap: 1, minHeight: 56 }}>
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ color: '#111827', width: 44, height: 44 }} aria-label="open navigation">
              <MenuIcon />
            </IconButton>
            {!company.loading && company.logo && <Box component="img" src={company.logo} alt={company.shortName || company.name || 'Company logo'} onError={(event) => { event.currentTarget.style.display = 'none'; }} sx={{ height: 26, width: 'auto' }} />}
            <Typography sx={{ fontWeight: 900, fontSize: '1rem', letterSpacing: 0.5, lineHeight: 1 }}>
              <Box component="span" sx={{ color: '#2b2b2b' }}>ATS</Box>
              <Box component="span" sx={{ color: '#3DAC5A', ml: 0.5 }}>HR</Box>
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Permanent rail on desktop, off-canvas temporary drawer on mobile. */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: { md: drawerWidth },
          flexShrink: 0,
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          overflowX: 'hidden',
          p: { xs: 1.5, sm: 2, md: 3 },
          background: '#eef1f6',
          minHeight: '100vh',
          // Employee dashboard: unify every page to a single text weight (600) so
          // all employee pages share one consistent font style. The descendant
          // selector outranks each component's own weight. Scoped to the employee
          // layout — admin/HR pages use a different layout and are unaffected.
          '& .MuiTypography-root': { fontWeight: 600 },
        }}
      >
        {/* Spacer that clears the fixed mobile app bar. */}
        {isMobile && <Toolbar sx={{ minHeight: 56 }} />}

        {/* Shared profile top bar — shown above every employee page except
            Profile and Form 16, which carry their own identity headers. */}
        {!hideTopNav && <EmployeeTopNav />}

        <Emproutes />

      </Box>

      {process.env.REACT_APP_ENABLE_LUMA_AI === 'true'
        ? <AiHrAssistant />
        : <AskHRWidget />}
    </Box>
  );
}
